import { fetchServer } from "@/features/utils/fetch";
import {
  CryptoAssetsResponse,
  GenerateCryptoAmountQuoteProps,
  GenerateWalletDepositProps,
  GenerateWalletDepositResponse,
  GetWalletDataResponse,
  SchemaRequestCreateTravelRule,
  SendToKypayWallettReponse,
  walletsTravelRules,
  type CreateWalletReq,
  type CreateWalletResponse,
  type FeedKypayBalanceCardSessioneResponse,
  type FeedKypayBalanceCardSessionProps,
  type FeedKypayBalanceProps,
  type FeedKypayBalanceResponse,
  type GetWalleTokenyTransactionResponse,
  type GetWalletPolygon2LausanneResponse,
  type Kypay,
  type KypayAccount,
  type SearchOlkyPassIdentity,
  type SendKypayToUserWalletProps,
  type SendKypayToUserWalletReponse,
  type SendToKypayProps,
  type SendToKypayReponse,
  type TransactionResponse,
  type TransactionStatus,
  type TransferKypayAmountProps,
  type TransferKypayAmountResponse,
  type WithdrawKypayBalanceProps,
  type WithdrawKypayBalanceResponse
} from "../types";
import { ApiError } from "@/utils/internal-code-handler";

export class KypayApiClient {
  static async getKypay(contextLegalEntityUuid?: string): Promise<Kypay> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/user/kypay?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : `/user/kypay`,

      {
        method: "GET",
        reloadOn401: false
      }
    );
    return await response.json();
  }
  static async getTransaction(
    page?: number,
    limit?: number,
    contextLegalEntityUuid?: string
  ): Promise<TransactionResponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/kypay/transactions?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : `/kypay/transactions`,
      {
        method: "POST",
        reloadOn401: false,
        body: {
          pagination: {
            item_per_page: limit,
            page: page
          },
          limit: limit
        }
      }
    );
    return await response.json();
  }
  static async getPrefetchKypay(nextPage?: number, maxPage?: number): Promise<Kypay> {
    const response = await fetchServer(`/user/kypay?page=${nextPage}&limit=${maxPage}`, {
      method: "GET",
      reloadOn401: false
    });
    return (await response.json()) as Kypay;
  }

  static async getCryptoAssets(): Promise<CryptoAssetsResponse> {
    const response = await fetchServer("/kypay/crypto/assets", {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });
    const data = (await response.json()) as CryptoAssetsResponse;
    return data;
  }
  static async createTravelRuleWalletsQuery(
    data: SchemaRequestCreateTravelRule,
    contextLegalEntityUuid?: string
  ): Promise<{
    success: boolean;
    data: walletsTravelRules;
  }> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/kypay/crypto/travel-rules?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : "/kypay/crypto/travel-rules",
      {
        method: "POST",
        reloadOn401: false,
        mainRoot: false,
        body: data
      }
    );
    if (!response.ok) {
      throw new Error("wallet rule failed");
    }

    const travelWalletCreateResp = await response.json();

    return travelWalletCreateResp;
  }
  static async getTravelRuleWalletsQuery(contextLegalEntityUuid?: string): Promise<{
    success: boolean;
    data: walletsTravelRules[];
  }> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/kypay/crypto/travel-rules?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : "/kypay/crypto/travel-rules",
      {
        method: "GET",
        reloadOn401: false,
        mainRoot: false
      }
    );
    if (!response.ok) {
      throw new Error("wallet rule failed");
    }

    const data = await response.json();

    return data;
  }
  static async getUserWalletTransactions(
    contextLegalEntityUuid?: string
  ): Promise<GetWalleTokenyTransactionResponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? "/kypay/web3/get_transactions?contextLegalEntityUuid=" + contextLegalEntityUuid
        : "/kypay/web3/get_transactions",
      {
        method: "GET",
        reloadOn401: false,
        mainRoot: false
      }
    );
    if (!response.ok) {
      throw new Error("transaction failed");
    }
    const data = (await response.json()) as GetWalleTokenyTransactionResponse;
    return data;
  }

  static async getUserWalletData(contextLegalEntityUuid?: string): Promise<GetWalletDataResponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? "/kypay/web3/wallet_data?contextLegalEntityUuid=" + contextLegalEntityUuid
        : "/kypay/web3/wallet_data",
      {
        method: "GET",
        reloadOn401: false,
        mainRoot: false
      }
    );

    if (!response.ok) throw new ApiError(await response.json(), response.status);
    return await response.json();
  }

  static async getWalletPolygon2Lausanne(): Promise<GetWalletPolygon2LausanneResponse> {
    const response = await fetchServer("/kypay/web3/address/polygon2lausanne", {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });
    const data = (await response.json()) as GetWalletPolygon2LausanneResponse;

    return data;
  }

  static async createUserWallet(
    wallet: CreateWalletReq,
    contextLegalEntityUuid?: string
  ): Promise<CreateWalletResponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? "/kypay/web3/wallet/create?contextLegalEntityUuid=" + contextLegalEntityUuid
        : "/kypay/web3/wallet/create",
      {
        method: "POST",
        body: wallet,
        reloadOn401: false,
        mainRoot: false
      }
    );
    return (await response.json()) as CreateWalletResponse;
  }
  static async createUserOlkyWallet(): Promise<CreateWalletResponse> {
    const response = await fetchServer("/kypay/web3/create_olky_wallet", {
      method: "POST",
      body: {},
      reloadOn401: false,
      mainRoot: false
    });
    return (await response.json()) as CreateWalletResponse;
  }
  static async sendKypayToUserWallet(
    sendData: SendKypayToUserWalletProps,
    contextLegalEntityUuid?: string
  ): Promise<SendKypayToUserWalletReponse> {
    const response = await fetchServer(
      !contextLegalEntityUuid
        ? "/kypay/web3/transfer"
        : "/kypay/web3/transfer?contextLegalEntityUuid=" + contextLegalEntityUuid,
      {
        method: "POST",
        body: sendData,
        reloadOn401: false,
        mainRoot: false
      }
    );

    if (!response.ok) {
      throw new Error();
    }

    return (await response.json()) as SendKypayToUserWalletReponse;
  }

  static async investToPartner(
    sendData: SendKypayToUserWalletProps,
    contextLegalEntityUuid?: string
  ): Promise<SendKypayToUserWalletReponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? "/partner/transfer_onchain?contextLegalEntityUuid=" + contextLegalEntityUuid
        : "/partner/transfer_onchain",
      {
        method: "POST",
        body: sendData,
        reloadOn401: false,
        mainRoot: false
      }
    );
    if (!response.ok) {
      throw new Error();
    }

    return (await response.json()) as SendKypayToUserWalletReponse;
  }

  static async sendToKyPay(
    sendData: SendToKypayProps,
    contextLegalEntityUuid?: string
  ): Promise<SendToKypayWallettReponse | SendToKypayReponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/kypay/web3/transfer_to_kypay?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : "/kypay/web3/transfer_to_kypay",
      {
        method: "POST",
        body: sendData,
        reloadOn401: false,
        mainRoot: false
      }
    );
    if (!response.ok) {
      throw new Error();
    }
    return (await response.json()) as SendToKypayWallettReponse | SendToKypayReponse;
  }

  static async feedKypayBalance(
    data: FeedKypayBalanceProps,
    contextLegalEntityUuid?: string
  ): Promise<FeedKypayBalanceResponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? "/kypay/fund_account?contextLegalEntityUuid=" + contextLegalEntityUuid
        : "/kypay/fund_account",
      { body: data }
    );
    if (!response.ok) {
      throw new Error();
    }

    return await response.json();
  }

  static async generateQuoteAmount(
    data: GenerateCryptoAmountQuoteProps,
    contextLegalEntityUuid?: string
  ): Promise<number> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/kypay/crypto/quote?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : "/kypay/crypto/quote",
      { body: data }
    );
    if (!response.ok) {
      throw new Error();
    }

    const res = await response.json();
    return res.data;
  }
  static async generateDepositWallet(
    data: GenerateWalletDepositProps,
    contextLegalEntityUuid?: string
  ): Promise<GenerateWalletDepositResponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/kypay/crypto/deposit-address?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : "/kypay/crypto/deposit-address",
      { body: data }
    );
    if (!response.ok) {
      throw new Error();
    }

    const res = await response.json();
    return res;
  }

  static async feedKypayBalanceCardSession(
    data: FeedKypayBalanceCardSessionProps,
    contextLegalEntityUuid?: string
  ): Promise<FeedKypayBalanceCardSessioneResponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/kypay/card?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : "/kypay/card ",
      { body: data }
    );
    if (!response.ok) {
      throw new Error();
    }

    return await response.json();
  }

  static async withdrawKypayBalance(
    data: WithdrawKypayBalanceProps,
    contextLegalEntityUuid?: string
  ): Promise<WithdrawKypayBalanceResponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? "/kypay/withdraw?contextLegalEntityUuid=" + contextLegalEntityUuid
        : "/kypay/withdraw",
      { body: data }
    );
    if (!response.ok) {
      throw new Error();
    }

    const res = await response.json();
    return res.data;
  }
  static async getKypayAccounts(contextLegalEntityUuid?: string): Promise<KypayAccount[]> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? "/kypay/my_accounts?contextLegalEntityUuid=" + contextLegalEntityUuid
        : "/kypay/my_accounts",
      {
        method: "GET",
        reloadOn401: false,
        mainRoot: false
      }
    );
    if (!response.ok) {
      throw new Error();
    }

    return (await response.json()) as KypayAccount[];
  }

  static async getTransactionStatus(
    transactionReference: string,
    contextLegalEntityUuid?: string
  ): Promise<TransactionStatus> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/kypay/card/${transactionReference}/status?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : `/kypay/card/${transactionReference}/status`,
      {
        method: "GET",
        reloadOn401: false,
        mainRoot: false
      }
    );
    if (!response.ok) {
      throw new Error();
    }
    return await response.json();
  }
  static async searchIdentities(keyword: string): Promise<SearchOlkyPassIdentity[]> {
    const response = await fetchServer(`/kypay/search_identities/${keyword}`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });
    if (!response.ok) {
      throw new Error();
    }

    return (await response.json()) as SearchOlkyPassIdentity[];
  }

  static async transferKypayAmount(
    data: TransferKypayAmountProps,
    contextLegalEntityUuid?: string
  ): Promise<TransferKypayAmountResponse> {
    const response = await fetchServer(
      contextLegalEntityUuid
        ? `/kypay/transfer_amount?contextLegalEntityUuid=${contextLegalEntityUuid}`
        : "/kypay/transfer_amount",
      { body: data }
    );
    if (!response.ok) {
      throw new Error();
    }
    return (await response.json()) as TransferKypayAmountResponse;
  }
}
